import axios from 'axios';
import { useQuery } from 'react-query';
import Header from '../../../shared/Header';
import { Flex, Stack } from '../../../shared/layouts';
import Placeholder from '../../../shared/Placeholder';
import Loader from '../../../shared/Loader';
import { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@dnhb/node_package';
import BackButton from '../../../shared/BackButton';
import DescriptionMessage from '../../../shared/DescriptionMessage';
import Card from '../../../shared/Card';
import ProgressBar from '../../../shared/ProgressBar';
import Button from '../../../shared/Button';
import MessagebarContext from '../../../../context/MessagebarContext';
import { PureDynamicForm } from '@dnhb/functional-final-form';
import {
  CheckBoxGridField,
  CheckBoxListField,
  DateField,
  InputField,
  TextAreaField,
  RadioButtonField,
  RadioGridField
} from '../../../shared/final-form-fields';
import { useTranslation } from 'react-i18next';
import AddItemBox from '../../../shared/AddItemBox';
import FormList from "./FormList";
import useIconLibrary from "../../../../hooks/useIconLibrary";

const QuestionnaireDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestId } = useParams();
  const { breakpoints } = useTheme();
  const isMinTablet = useMediaQuery(
    `(min-width: ${breakpoints.md ?? '37.5rem'})`
  );
  const { setAlertMessage, setSuccesMessage } = useContext(MessagebarContext);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const formRef = useRef(null);

  const { QuestionnaireIcon } = useIconLibrary();

  const {
    isError,
    isLoading,
    data: {
      title: pageTitle,
      assigned_person_name,
      description,
      questions = [],
      answers = {},
      introText,
      completionMessage,
      showLegend
    } = {}
  } = useQuery(
    ['QuestionnaireList', requestId],
    async () => {
      const { data } = await axios(`/questionnaire/${requestId}`);
      const {
        body: { questions, answers, introText, completionMessage, showLegend },
        ...pageData
      } = data;
      return { ...pageData, questions, answers, introText, completionMessage, showLegend };
    },
    { refetchOnWindowFocus: false, refetchOnReconnect: false }
  );

  useEffect(() => {
    const pagesAmount = questions.filter(({ formType }) => formType === 'page')
      ?.length;
    const existingIndex = answers?.pageIndex;
    const maxIndex = pagesAmount - 1;

    if (existingIndex) {
      setPageIndex(existingIndex > maxIndex ? maxIndex : existingIndex);
    } else if (introText || showLegend) {
      setPageIndex(-1);
    }
    setTotalPages(pagesAmount);
  }, [answers]);

  const currentPage = questions.find(
    ({ name }) => name === `page_${pageIndex}`
  );
  const lastQuestion = pageIndex === totalPages - 1;
  const headerTitle =
    pageIndex < 0
      ? pageTitle
      : t('Rubriek {{page}}/{{totalPages}} - {{title}}', {
          page: pageIndex + 1,
          totalPages,
          title: currentPage?.formProps?.title
        });

  const calculatePageSkip = (back = false) => {
    const addition = back ? -1 : 1;

    let pageSkip = 0;
    let nextPage = formRef?.current?.children?.[pageIndex + addition];
    while (nextPage && nextPage.style.getPropertyValue('display') === 'none') {
      pageSkip += addition;
      nextPage = formRef?.current?.children[pageIndex + addition + pageSkip];
    }

    return Math.abs(pageSkip);
  }

  return (
    <>
      {isLoading && <Loader />}
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        {requestId !== null && (
          <BackButton onClick={() => navigate('/requests')}>
            {t('Terug')}
          </BackButton>
        )}
        <Header
          title={
            t('Vragenlijst: {{pageTitle}}', { pageTitle }) +
            (assigned_person_name ? ` (${assigned_person_name})` : '')
          }
          icon={<QuestionnaireIcon height="2rem" width="2rem" />}
          variant="page"
        />
        {description && <DescriptionMessage>{description}</DescriptionMessage>}
        {isError || questions.length === 0 ? (
          <Placeholder>
            {t('Er is iets mis gegaan met het ophalen van deze vragenlijst.')}
          </Placeholder>
        ) : (
          <Card css={{ position: 'relative', overflow: 'hidden' }}>
            <Header title={headerTitle} variant="section" mb={'l'}>
              {currentPage?.description && (
                <p css={{ marginTop: 10, fontWeight: 'normal' }}>
                  {currentPage?.description}
                </p>
              )}
            </Header>
            {pageIndex < 0 ? (
              <>
                <div css={{ whiteSpace: 'pre-line'}}>
                  {introText}
                </div>
                {showLegend && (
                  <div css={{ marginLeft: 10 }}>
                    {questions.map((page, index) => (
                      <p key={`page-${index}`}>
                        {index + 1}. {page.formProps.title}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <PureDynamicForm
                  ref={formRef}
                  axiosConfig={{
                    method: 'PUT',
                    transformRequest: [
                      data => ({ ...data, pageIndex, pageSkip: calculatePageSkip()}),
                      ...axios.defaults.transformRequest
                    ]
                  }}
                  formDefinition={questions}
                  initialValues={answers}
                  url={`/questionnaire/${requestId}`}
                  afterSubmit={({ nextPageIndex }) => {
                    if (lastQuestion) {
                      axios
                        .put(`/questionnaire/${requestId}/complete`)
                        .then(() => {
                          setSuccesMessage(completionMessage ?? t('Vragenlijst afgerond'));
                          navigate('/requests');
                        })
                        .catch(() => setAlertMessage());
                    } else {
                      setPageIndex(index => nextPageIndex || index + 1 + calculatePageSkip());
                    }
                  }}
                  elementMap={{
                    text: InputField,
                    number: props => (
                      <InputField {...props} inputMode="numeric" />
                    ),
                    currency: props => <InputField {...props} isCurrency />,
                    date: DateField,
                    textarea: TextAreaField,
                    checkbox: CheckBoxListField,
                    'checkbox-grid': CheckBoxGridField,
                    radio: props => <RadioButtonField {...props} />,
                    'radio-grid': RadioGridField,
                    page: props => {
                      if (props?.name === `page_${pageIndex}`) {
                        return <Stack {...props} space={'xl'} />;
                      }
                      return null;
                    },
                    AddButton: props => (
                      <AddItemBox
                        title={`${props?.item} toevoegen`}
                        {...props}
                      />
                    ),
                    list: props => <FormList {...props} />
                  }}
                />
              </>
            )}

            <Flex
              mt="xl"
              justifyContent="space-between"
              flexDirection={isMinTablet ? 'row-reverse' : 'column'}
              css={{ '> * + *': { marginTop: !isMinTablet ? '16px' : null } }}
            >
              {/* Buttons for testing */}
              <Button
                variant="primary"
                buttonSize="small"
                width={!isMinTablet ? '100%' : null}
                onClick={() =>
                  pageIndex < 0
                    ? setPageIndex(0)
                    : formRef.current.requestSubmit()
                }
              >
                {lastQuestion
                  ? t('Verstuur en sluit af')
                  : pageIndex < 0
                  ? t('Start')
                  : t('Volgende')}
              </Button>
              {pageIndex > 0 && (
                <Button
                  variant="primary-outline"
                  buttonSize="small"
                  width={!isMinTablet ? '100%' : null}
                  onClick={() => setPageIndex(index => index - 1 - calculatePageSkip(true))}
                >
                  {t('Vorige')}
                </Button>
              )}
            </Flex>

            <ProgressBar
              value={pageIndex}
              total={totalPages}
              css={{
                position: 'absolute',
                left: 0,
                top: isMinTablet ? null : 0,
                bottom: isMinTablet ? 0 : null,
                width: '100%',
                background: 'transparent'
              }}
            />
          </Card>
        )}
      </Stack>
    </>
  );
};

export default QuestionnaireDetailPage;
